import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Switch } from "@headlessui/react";
import { ErrorMessage, Form, Formik, Field, FieldInputProps, FieldArray } from "formik";
import React, { useState } from "react";
import CheckboxField from '../fields/CheckboxField';
import { FileReference, UploadStatus, makeFileKey } from '../fields/FileUpload';
import { ProfileType } from "../../types/ProfileType";
import * as yup from 'yup';
import VerticalStepper from "../VerticalStepper";
import { Space } from "../../types/Space";
import { ProfilePrivacy } from "../../types/ProfilePrivacy";
import { faExclamationCircle } from "@fortawesome/pro-light-svg-icons";
import { constants } from '../../constants';
import Link from 'next/link';
import { ProfilePictureUpload } from '../fields/ProfilePictureUpload';
import { Button } from '../utilities/Button';
import BannerLibraryPicker from '../fields/BannerLibraryPicker';
import { BannerPictureUpload } from '../fields/BannerPictureUpload';
import { PreviewFileReference } from '../fields/PreviewFileReference';
import { faSave, faUserPlus, faUserMinus, faUserTag, faPalette, faUserLock, faSignature } from '@fortawesome/pro-regular-svg-icons';
import { Info } from 'luxon';

interface SpaceEditorProps {
    space: Space,
    isNew: boolean,
    // Needs to be passed as Formik has no clue about GQL
    isSaving: boolean,
    saveSpace: (spaceValues: SpaceEditorValues) => void
}

export interface SpaceEditorValues {
    profileType: ProfileType,
    name: string,
    location: string,
    locationVisible: boolean,
    yearOfBirth?: number,
    yearOfDeath?: number,
    //birthdayDay?: number,
    //birthdayMonth?: number,
    profilePicture: FileReference | null,
    background: FileReference | null,
    obituary: string,
    privacy: ProfilePrivacy,
    moderators: string[],
    administrators: string[],
    agreeTos: boolean
}

export default function SpaceEditor({ space, isNew, isSaving, saveSpace }: SpaceEditorProps) {
    const [currentSection, setCurrentSection] = useState<string | number>(1);

    const currentYear = new Date(Date.now()).getFullYear();

    const formDefaults: SpaceEditorValues = {
        profileType: space.profileType || ProfileType.Person,
        name: space.name || '',
        location: space.location || '',
        locationVisible: space.locationVisible || true,
        yearOfBirth: space.yearOfBirth || undefined,
        yearOfDeath: space.yearOfDeath || undefined,
        //birthdayDay: space.birthdayDay || undefined,
        //birthdayMonth: space.birthdayMonth || undefined,
        profilePicture: !space.profilePicture
            ? null
            : {
                key: makeFileKey(space.profilePicture.url),
                hostFilename: space.profilePicture.url,
                localFile: new File([], space.profilePicture.url),
                previewUrl: constants.cdnBaseUrl + '/' + space.profilePicture.url,
                uploadStatus: UploadStatus.Completed
            },
        background: !space.background
            ? null
            : {
                key: makeFileKey(space.background.url),
                hostFilename: space.background.url,
                localFile: new File([], space.background.url),
                previewUrl: constants.cdnBaseUrl + '/' + space.background.url,
                uploadStatus: UploadStatus.Completed
            },
        obituary: space.obituary || '',
        privacy: space.profilePrivacy || ProfilePrivacy.Public,
        moderators: space.moderators || [],
        administrators: space.administrators || [],
        agreeTos: !isNew
    };

    const schema = yup.object().shape({
        name: yup.string().required('You must provide a name.'),
        yearOfBirth: yup.number()
            .nullable(true)
            .min(1900, 'The year of birth should be 1900 or greater')
            .max(currentYear, 'The year of birth cannot be greater than the current year')
            .typeError('The entered value for year of birth is not a valid year'),
        yearOfDeath: yup.number()
            .nullable(true)
            .min(yup.ref('yearOfBirth'), 'The year of death should be equal to or greater than the year of birth')
            .max(currentYear, 'The year of death cannot be greater than the current year')
            .typeError('The entered value for year of death is not a valid year'),
        agreeTos: yup.boolean().isTrue('You must agree to the terms of service to continue.')
    });

    const lastPageNumber = isNew ? 4 : 3;
    const backHref = isNew ? '/' : `/spaces/${space.id}`;

    const steps = [
        {
            id: 1,
            title: 'Profile information',
            description: 'Please provide some basic information to make the place easier to find',
            icon: faUserTag
        },
        {
            id: 2,
            title: 'Customise your place',
            description: 'Customise your place by adding a profile picture, background image and biography',
            icon: faPalette
        },
        {
            id: 3,
            title: 'Privacy settings',
            description: 'Configure the privacy options and assign additional administrators and moderators to the place',
            icon: faUserLock
        }
    ];

    if (isNew)
        steps.push({
            id: 4,
            title: 'Terms of service',
            description: 'Finally, we need you to read and agree to our terms of service',
            icon: faSignature
        });

    const daysOfMonth = React.useMemo(() => Array.from(Array(31).keys()).map(n => n + 1), []);
    const monthNames = React.useMemo(() => Info.months('long'), []);

    return (
        <div className='flex flex-col-reverse lg:flex-row lg:space-x-16'>
            <div className='border-t border-gray-200 pt-6 lg:w-8/12 lg:max-w-3xl lg:border-t-0 lg:pt-0'>
                <Formik
                    initialValues={formDefaults}
                    validationSchema={schema}
                    onSubmit={saveSpace}>
                    {({ values, errors, touched, setFieldValue, isValid }) =>
                        <Form>
                            <div className='space-y-10'>
                                {currentSection === 1 && (<>
                                    <div>
                                        <label className='text-loving-500 font-medium text-lg'>Is this place for a person or pet?</label>
                                        <div className='text-sm max-w-prose text-gray-600'>This will make it easier for people to find the place later on.</div>
                                        <div className='flex space-x-8 mt-4'>
                                            <div className='inline-flex items-center'>
                                                <Field type='radio' id='person' value='PERSON' name='profileType' className='text-loving-500 focus:ring-loving-500' />
                                                <label htmlFor='person' className='font-medium text-gray-700 ml-2'>Person</label>
                                            </div>
                                            <div className='inline-flex items-center'>
                                                <Field type='radio' id='pet' value='PET' name='profileType' className='text-loving-500 focus:ring-loving-500' />
                                                <label htmlFor='pet' className='font-medium text-gray-700 ml-2'>Pet</label>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <label className='text-loving-500 font-medium text-lg'>What was their name?</label>
                                        <Field type='text' name='name' className={`mt-2 block w-80 max-w-full shadow-sm sm:text-sm ${errors.name ? 'border-red-400' : 'border-gray-300'} rounded-md focus:ring-loving-500 focus:border-loving-500 ${errors.name && 'bg-red-50'}`} />
                                        <div className='block mt-1 font-medium text-red-500 text-xs'>
                                            <ErrorMessage name='name' />
                                        </div>
                                    </div>
                                    <div>
                                        <label className='text-loving-500 font-medium text-lg'>Where did {values.name ? values.name : 'they'} live?</label>
                                        <Field type='text' name='location' className={`mt-2 block w-96 max-w-full shadow-sm sm:text-sm ${errors.location ? 'border-red-400' : 'border-gray-300'} rounded-md focus:ring-loving-500 focus:border-loving-500 ${errors.location && 'bg-red-50'}`} />
                                        <div className='text-xs max-w-prose mt-2 text-gray-600'>This is optional, but will help others find the place when searching. You don&apos;t need to provide a full address, just a town or village will do.</div>
                                        <div className='block mt-1 font-medium text-red-500 text-xs'>
                                            <ErrorMessage name='location' />
                                        </div>
                                        <Field type='checkbox' name='locationVisible'>
                                            {({ field }: { field: FieldInputProps<boolean> }) => (
                                                <Switch.Group>
                                                    <div className='flex items-center mt-4'>
                                                        <Switch
                                                            checked={values.locationVisible}
                                                            onChange={(checked: boolean) => setFieldValue(field.name, checked)}
                                                            className={`${values.locationVisible ? 'bg-loving-500' : 'bg-gray-200'} relative inline-flex items-center h-6 rounded-full w-11 focus:outline-none`}>
                                                            <span className='sr-only'>Location visible</span>
                                                            <span className={`${values.locationVisible ? 'translate-x-6' : 'translate-x-1'} inline-block w-4 h-4 transform transition ease-in-out duration-200 bg-white rounded-full`} />
                                                        </Switch>
                                                        <Switch.Label className='ml-3 font-medium text-gray-700 text-sm'>Location visible to the public</Switch.Label>
                                                    </div>
                                                </Switch.Group>
                                            )}
                                        </Field>
                                    </div>
                                    <div>
                                        <label className='text-loving-500 font-medium text-lg'>What years did {values.name ? values.name + "'s" : 'their'} life span?</label>
                                        <div className='text-sm max-w-prose text-gray-600'>Both fields are optional, but providing this information can make it easier for others to find the place.</div>
                                        <div className='flex space-x-4 mt-3 items-center'>
                                            <Field
                                                type='text'
                                                name='yearOfBirth'
                                                placeholder='YYYY'
                                                maxLength={4}
                                                className={`block w-20 font-medium text-gray-700 text-center shadow-sm rounded-md focus:ring-loving-500 focus:border-loving-500 ${errors.yearOfBirth ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} />
                                            <div className='w-6 h-px bg-gray-300' />
                                            <Field
                                                type='text'
                                                name='yearOfDeath'
                                                placeholder='YYYY'
                                                maxLength={4}
                                                className={`block w-20 font-medium text-gray-700 text-center shadow-sm rounded-md focus:ring-loving-500 focus:border-loving-500 ${errors.yearOfDeath ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} />
                                        </div>
                                        <div className='block mt-1 font-medium text-red-500 text-xs'>
                                            <ErrorMessage name='yearOfBirth' />
                                        </div>
                                        <div className='block mt-1 font-medium text-red-500 text-xs'>
                                            <ErrorMessage name='yearOfDeath' />
                                        </div>
                                    </div>
                                    {/* <div>
                                    <label className='text-loving-500 font-medium text-lg'>When was {values.name ? values.name + "'s" : 'their'} birthday?</label>
                                    <div className='mt-4'>
                                        <select
                                            name='birthdayDay'
                                            className={`font-medium text-gray-700 shadow-sm rounded-md focus:ring-loving-500 focus:border-loving-500 ${errors.yearOfDeath ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}>
                                            <option value={undefined}></option>
                                            {daysOfMonth.map(d => (
                                                <option key={d} value={d}>{d}</option>
                                            ))}
                                        </select>
                                        <select
                                            name='birthdayMonth'
                                            className={`ml-2 font-medium text-gray-700 shadow-sm rounded-md focus:ring-loving-500 focus:border-loving-500 ${errors.yearOfDeath ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}>
                                            {monthNames.map((m, index) => (
                                                <option key={m} value={index + 1}>{m}</option>
                                            ))}
                                        </select>
                                    </div>
                                    </div> */}
                                </>)}
                                {currentSection === 2 && (<>
                                    <div>
                                        <label className='text-loving-500 font-medium text-lg'>Upload a profile picture for {values.name || 'them'}</label>
                                        <div className='text-sm text-gray-600'>This will help users identify the correct place in search results, and will appear on the place itself.</div>
                                        {/* <FileUpload maxFiles={1} maxSize={4 * 1000 * 1000} name='profilePicture' label='Upload profile picture' initialFiles={formDefaults.profilePicture} setFieldValue={setFieldValue} /> */}
                                        <ProfilePictureUpload
                                            name='profilePicture'
                                            label='Upload profile picture'
                                            maxSize={1 * 1000 * 1000}
                                            currentProfilePicture={formDefaults.profilePicture}
                                            setFieldValue={setFieldValue} />
                                    </div>
                                    <div>
                                        <label className='text-loving-500 font-medium text-lg'>Upload or select a background image for the place</label>
                                        <div className='text-sm text-gray-600 mb-1'>Personalise the place by either uploading your own banner image, or selecting one from our library.</div>
                                        <div className='text-xs text-gray-600 mb-6 italic'>Banner images will be automatically resized and cropped; we recommend uploading an image that is at least 1600px wide and 400px high to ensure acceptable image quality across most devices.</div>
                                        <div className='flex items-center'>
                                            <div>
                                                {/* <FileUpload
                                                    maxFiles={1}
                                                    maxSize={5 * 1000 * 1000}
                                                    name='background'
                                                    label='Upload background image'
                                                    initialFiles={formDefaults.background}
                                                    setFieldValue={setFieldValue}
                                                    disabled={values.background.length >= 1} /> */}
                                                <BannerPictureUpload
                                                    name='background' />
                                            </div>
                                            <em className='mx-4 text-gray-400'>or</em>
                                            <div>
                                                <BannerLibraryPicker currentBackground={values.background} onSelect={image => {
                                                    setFieldValue('background', {
                                                        hostFilename: image,
                                                        previewUrl: `${constants.cdnBaseUrl}/${image}`,
                                                        uploadStatus: UploadStatus.Completed
                                                    } as FileReference)
                                                }} />
                                            </div>
                                        </div>
                                        {values.background && (
                                            <>
                                                <div className='mt-4 text-xs font-semibold text-gray-700'>Current banner image</div>
                                                <div className='text-xs text-gray-600'>Click to remove</div>
                                                <PreviewFileReference
                                                    fileReference={values.background}
                                                    removeFile={_ => setFieldValue('background', null)} />
                                            </>
                                        )}
                                    </div>
                                    <div>
                                        <label className='text-loving-500 font-medium text-lg'>Write a short biography for {values.name || 'them'}</label>
                                        <div className='text-sm text-gray-600'>This will appear at the top of the place for all visitors and serves as a welcome message.</div>
                                        <Field type='text' component='textarea' name='obituary' className={`mt-2 h-48 w-full block max-w-full shadow-sm sm:text-sm ${errors.obituary ? 'border-red-400' : 'border-gray-300'} rounded-md focus:ring-loving-500 focus:border-loving-500 ${errors.obituary && 'bg-red-50'}`} />
                                        <div className='block mt-1 font-medium text-red-500 text-xs'>
                                            <ErrorMessage name='obituary' />
                                        </div>
                                    </div>
                                </>)}
                                {currentSection === 3 && (<>
                                    <div>
                                        <label className='text-loving-500 font-medium text-lg'>Select a privacy setting for the place</label>
                                        <div className='text-sm text-gray-600'>This can be changed at any time via the place settings.</div>
                                        <div className='flex flex-col space-y-2 mt-4'>
                                            <div className='items-start inline-flex'>
                                                <Field type='radio' id='privacy-public' value='PUBLIC' name='privacy' className='mt-1 text-loving-500 focus:ring-loving-500' />
                                                <div className='ml-2'>
                                                    <label htmlFor='privacy-public' className='font-medium text-gray-700'>Public</label>
                                                    <div className='text-sm text-gray-600'>Anyone can view and contribute to the place.</div>
                                                </div>
                                            </div>
                                            <div className='items-start inline-flex'>
                                                <Field type='radio' id='privacy-unlisted' value='UNLISTED' name='privacy' className='mt-1 text-loving-500 focus:ring-loving-500' />
                                                <div className='ml-2'>
                                                    <label htmlFor='privacy-unlisted' className='font-medium text-gray-700'>Unlisted</label>
                                                    <div className='text-sm text-gray-600'>Hides the place from search results, but is otherwise public.</div>
                                                </div>
                                            </div>
                                            <div className='items-start inline-flex'>
                                                <Field type='radio' id='privacy-readonly' value='READONLY' name='privacy' className='mt-1 text-loving-500 focus:ring-loving-500' />
                                                <div className='ml-2'>
                                                    <label htmlFor='privacy-readonly' className='font-medium text-gray-700'>Read only</label>
                                                    <div className='text-sm text-gray-600'>The place can be viewed publicly but only registered owners/contributors can post.</div>
                                                </div>
                                            </div>
                                            <div className='items-start inline-flex'>
                                                <Field type='radio' id='privacy-private' value='PRIVATE' name='privacy' className='mt-1 text-loving-500 focus:ring-loving-500' />
                                                <div className='ml-2'>
                                                    <label htmlFor='privacy-private' className='font-medium text-gray-700'>Private</label>
                                                    <div className='text-sm text-gray-600'>The place can only be viewed by you and other registered owners/contributors.</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <label className='text-loving-500 font-medium text-lg'>Invite additional moderators and administrators</label>
                                        <div className='text-sm text-gray-600'>They will have permissions to manage and maintain the place. Access permissions can always be managed at any time from the place settings. To invite a user, please enter the email address they used to register for the {constants.siteTitle} service.</div>
                                        <FieldArray name='administrators' render={arrayHelpers => (<>
                                            <div className='flex mt-4 space-x-2'>
                                                <div className='flex-auto'>
                                                    <div className='text-loving-500 font-medium'>
                                                        Administrators
                                                    </div>
                                                    <div className='text-sm text-gray-600'>
                                                        Administrators will have full control of the place and can invite additional users and administrators or moderators.
                                                    </div>
                                                </div>
                                                <button type='button' className='btn flex-none my-auto' onClick={() => arrayHelpers.push('')}>
                                                    <FontAwesomeIcon icon={faUserPlus} className='mr-2' />
                                                    New
                                                </button>
                                            </div>
                                            {values.administrators.map((administrator, index) => (
                                                <div className='flex mt-2 space-x-2' key={index}>
                                                    <Field type='text' name={`administrators.${index}`} placeholder='Email address' className='inline-flex flex-grow max-w-full shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-loving-500 focus:border-loving-500' />
                                                    <Button
                                                        variant='outline'
                                                        icon={faUserMinus}
                                                        text='Remove'
                                                        onClick={_ => arrayHelpers.remove(index)} />
                                                </div>
                                            ))}
                                        </>)
                                        } />
                                        <FieldArray name='moderators' render={arrayHelpers => (<>
                                            <div className='flex mt-4 space-x-2'>
                                                <div className='flex-auto'>
                                                    <div className='text-loving-500 font-medium'>
                                                        Moderators
                                                    </div>
                                                    <div className='text-sm text-gray-600'>
                                                        Moderators are able to manage posts on a place, including viewing reports and removing posts.
                                                    </div>
                                                </div>
                                                <button type='button' className='btn flex-none my-auto' onClick={() => arrayHelpers.push('')}>
                                                    <FontAwesomeIcon icon={faUserPlus} className='mr-2' />
                                                    New
                                                </button>
                                            </div>
                                            {values.moderators.map((moderator, index) => (
                                                <div className='flex mt-2 space-x-2' key={index}>
                                                    <Field type='text' name={`moderators.${index}`} placeholder='Email address' className='inline-flex flex-grow max-w-full shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-loving-500 focus:border-loving-500' />
                                                    <Button
                                                        variant='outline'
                                                        icon={faUserMinus}
                                                        text='Remove'
                                                        onClick={_ => arrayHelpers.remove(index)} />
                                                </div>
                                            ))}
                                        </>)
                                        } />
                                    </div>
                                </>)}
                                {currentSection === 4 && (<>
                                    <div>
                                        <label className='text-loving-500 font-medium text-lg'>Finally, we just need you to review and agree to our terms of service.</label>
                                        <div className='w-full max-h-80 overflow-y-auto my-2 px-3 py-2 shadow-sm sm:text-sm border border-gray-300 rounded-md focus:ring-loving-500 focus:border-loving-500'>
                                            <label className='font-medium text-lg'>Terms of service</label>
                                            <p className='mt-1'>
                                                Welcome to Light up a Life - the place for anyone who has lost a family member (human or fluffy) to post and share memories, celebrate special occasions and keep that person or pet&apos;s light shining bright. The website has been developed by St Oswald&apos;s Hospice, and is made available to everyone without charge. It is run and moderated by St Oswald&apos;s staff and volunteers.
                                            </p>
                                            <p className='mt-1'>
                                                Please DO NOT
                                            </p>
                                            <ul className='mt-1 list-disc pl-8'>
                                                <li>Promote anyone else&apos;s products or services</li>
                                                <li>Promote your own business or service</li>
                                                <li>Use the website as a marketing channel or focus group, or to harvest information for your business</li>
                                            </ul>
                                            <p className='mt-1'>
                                                We want people to use Light up a Life to express themselves and to share content that is important to them, but not at the expense of the safety and well-being of others or the integrity of our community.
                                            </p>
                                            <p className='mt-1'>
                                                You therefore agree not to engage in the conduct described below (or to facilitate or support others in doing so):
                                            </p>
                                            <ul className='mt-1 list-disc pl-8'>
                                                <li>That breaches these terms</li>
                                                <li>That is unlawful, misleading, discriminatory or fraudulent</li>
                                                <li>That infringes or breaches someone else&apos;s rights, including their intellectual property rights</li>
                                                <li>You may not upload viruses or malicious code, or do anything that could disable, overburden or impair the proper working or appearance of our website</li>
                                            </ul>
                                            <p className='mt-1'>
                                                We can remove or block content that is in breach of these provisions.<br />
                                                To help support our community, we encourage you to report content or conduct that you believe breaches your rights (including intellectual property rights) or our terms and policies.<br />
                                                We also can remove or restrict access to your content, services or information if we determine that doing so is reasonably necessary to avoid or mitigate adverse legal or regulatory impacts to St Oswald&apos;s Hospice.<br />
                                                Posts that contravene the rules will be deleted and offenders will be removed from the website.
                                            </p>
                                            <p className='mt-1'>
                                                Moderators have day jobs, and so won&apos;t always be able to respond to requests to explain why a post has been deleted or a member has been removed. This is a supportive, friendly website and any abuse against others will not be tolerated.
                                            </p>
                                            <p className='mt-1'>
                                                We will collect and process personal information about you as part of your registration for this website. Details of how we use this information can be obtained at our <Link href="/privacy-policy"><a className='text-loving-500' >Privacy Policy</a></Link>, which also contains details about how you can change how we deal with your personal information.
                                            </p>
                                        </div>
                                        <CheckboxField name='agreeTos' label='I have read and agree to the above terms of service' />
                                        <div className='block mt-1 font-medium text-red-500 text-xs'>
                                            <ErrorMessage name="agreeTos" />
                                        </div>
                                    </div>
                                </>)}
                                <div className="flex">
                                    <div className='flex-1 space-x-2'>
                                        {currentSection === 1 && (
                                            <Link href={backHref}>
                                                <a>
                                                    <button type='button' className='btn'>Cancel</button>
                                                </a>
                                            </Link>)}
                                        {currentSection > 1 && (<button type='button' className='btn' onClick={() => setCurrentSection(currentSection as number - 1)}>Back</button>)}

                                        {currentSection < lastPageNumber && (
                                            <button
                                                type='button'
                                                className='btn btn-primary'
                                                onClick={() => setCurrentSection(currentSection as number + 1)}>
                                                Next
                                            </button>
                                        )}
                                        {currentSection === lastPageNumber && (
                                            <button
                                                type='submit'
                                                className='btn btn-primary'
                                                disabled={
                                                    !isValid ||
                                                    isSaving ||
                                                    (values.background ? values.background.uploadStatus !== UploadStatus.Completed : false) ||
                                                    (values.profilePicture ? values.profilePicture.uploadStatus !== UploadStatus.Completed : false)
                                                }>
                                                {isNew ? 'Create' : 'Save'}
                                            </button>
                                        )}
                                    </div>
                                    <div>
                                        {!isNew && (
                                            <Button
                                                variant='outline'
                                                icon={faSave}
                                                text='Save changes'
                                                type='submit'
                                                disabled={
                                                    !isValid ||
                                                    isSaving ||
                                                    (values.background ? values.background.uploadStatus !== UploadStatus.Completed : false) ||
                                                    (values.profilePicture ? values.profilePicture.uploadStatus !== UploadStatus.Completed : false)
                                                } />
                                        )}
                                    </div>
                                </div>

                                {/* Show the go back error if on the last page and there is an error other than the TOS one */}
                                {currentSection === lastPageNumber && !isValid && !errors.agreeTos &&
                                    <div className='bg-red-100 text-red-900 text-sm p-4'>
                                        <FontAwesomeIcon icon={faExclamationCircle} size='lg' className='mr-3 text-red-600' />
                                        Please go back and check the previous pages for errors.
                                    </div>
                                }
                            </div>
                        </Form>}
                </Formik>
            </div>
            <div className='mb-16 lg:w-4/12 lg:mb-0'>
                <VerticalStepper steps={steps}
                    activeStep={currentSection}
                    onSelection={target => setCurrentSection(target)} />
            </div>
        </div>
    );
}
